import { ItemCardService } from './itemCardService';

export class AssignmentRewardService {
  private userId: string;
  private classId: string;
  private itemCardService: ItemCardService;

  constructor(userId: string, classId: string) {
    this.userId = userId;
    this.classId = classId;
    this.itemCardService = new ItemCardService(userId, classId);
  }

  /**
   * 과제 완료 시 아이템 카드 지급
   */
  async grantCompletionRewards(
    assignmentId: string,
    studentId: string,
    completionRewards: string[]
  ): Promise<void> {
    if (!completionRewards || completionRewards.length === 0) {
      return;
    }

    try {
      console.log('🎁 완료 보상 지급 시작:', {
        assignmentId,
        studentId,
        completionRewards
      });

      // 아이템 카드 정보 로드
      const itemCardSet = await this.itemCardService.loadItemCardSet();
      if (!itemCardSet) {
        console.error('❌ 아이템 카드 세트를 찾을 수 없습니다.');
        return;
      }

      const allItemCards = [...itemCardSet.levelupItem, ...itemCardSet.storeItem];
      const rewardItems = allItemCards.filter(item => 
        completionRewards.includes(item.itemCardId)
      );

      console.log('🎁 지급할 아이템 카드들:', rewardItems);

      // 멤버 존재 여부 확인 (현재 선택된 그룹 기준)
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(this.userId, this.classId);
      const member = await partitionService.getMember(studentId);
      if (!member) {
        console.error('❌ 멤버 정보를 찾을 수 없습니다.');
        return;
      }

      // 각 아이템 카드를 멤버에게 지급
      for (const itemCard of rewardItems) {
        const instanceId = `${itemCard.itemCardId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const newItemCard = {
          instanceId,
          itemCardId: itemCard.itemCardId,
          type: itemCard.type,
          remainingUseCount: itemCard.type === 'active' ? itemCard.useCount : undefined,
          acquiredAt: new Date()
        };

        await this.addItemCardToStudent(studentId, newItemCard);
        console.log('✅ 아이템 카드 지급 완료:', {
          itemCardId: itemCard.itemCardId,
          itemName: itemCard.name,
          instanceId
        });
      }

      console.log('🎁 모든 완료 보상 지급 완료');
    } catch (error) {
      console.error('❌ 완료 보상 지급 실패:', error);
      throw error;
    }
  }

  /**
   * 과제 미완료 시 아이템 카드 회수
   */
  async revokeCompletionRewards(
    assignmentId: string,
    studentId: string,
    completionRewards: string[]
  ): Promise<void> {
    if (!completionRewards || completionRewards.length === 0) {
      return;
    }

    try {
      console.log('🔄 완료 보상 회수 시작:', {
        assignmentId,
        studentId,
        completionRewards
      });

      // 멤버 정보 로드 (현재 선택된 그룹 기준)
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(this.userId, this.classId);
      const member = await partitionService.getMember(studentId);
      if (!member) {
        console.error('❌ 멤버 정보를 찾을 수 없습니다.');
        return;
      }

      // 해당 과제의 완료 보상 아이템 카드들을 찾아서 제거
      const currentItemCards = Array.isArray(member.itemCards) ? member.itemCards : [];
      if (currentItemCards.length === 0) {
        console.log('📝 멤버에게 아이템 카드가 없습니다.');
        return;
      }

      const itemCardsToRemove = currentItemCards.filter((itemCard: any) => 
        completionRewards.includes(itemCard.itemCardId)
      );

      console.log('🔄 제거할 아이템 카드들:', itemCardsToRemove);

      // 각 아이템 카드를 멤버에게서 제거
      for (const itemCard of itemCardsToRemove) {
        await this.removeItemCardFromStudent(studentId, itemCard.instanceId);
        console.log('✅ 아이템 카드 회수 완료:', {
          itemCardId: itemCard.itemCardId,
          instanceId: itemCard.instanceId
        });
      }

      console.log('🔄 모든 완료 보상 회수 완료');
    } catch (error) {
      console.error('❌ 완료 보상 회수 실패:', error);
      throw error;
    }
  }

  /**
   * 멤버 문서 가져오기
   */
  // NOTE: 기존 단일 문서(ClassMembers-1)의 students 맵 구조는 더 이상 사용하지 않습니다.
  // 멤버 정보는 ClassMembersPartitionService의 members 배열(파티션)로 관리됩니다.

  /**
   * 멤버에게 아이템 카드 추가
   */
  private async addItemCardToStudent(studentId: string, itemCard: any): Promise<void> {
    try {
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(this.userId, this.classId);
      await partitionService.addStudentItemCardAtomic(studentId, itemCard);
    } catch (error) {
      console.error('❌ 아이템 카드 추가 실패:', error);
      throw error;
    }
  }

  /**
   * 멤버에게서 아이템 카드 제거
   */
  private async removeItemCardFromStudent(studentId: string, instanceId: string): Promise<void> {
    try {
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(this.userId, this.classId);
      await partitionService.updateStudentItemCardsAtomic(studentId, (currentCards) =>
        currentCards.filter((item: any) => item?.instanceId !== instanceId)
      );
    } catch (error) {
      console.error('❌ 아이템 카드 제거 실패:', error);
      throw error;
    }
  }

  /**
   * 과제 완료 상태 확인 및 보상 처리
   */
  async checkAndProcessRewards(
    assignmentId: string,
    studentId: string,
    newStatus: string,
    completionRewards: string[]
  ): Promise<void> {
    if (!completionRewards || completionRewards.length === 0) {
      return;
    }

    try {
      console.log('🔍 보상 처리 확인:', {
        assignmentId,
        studentId,
        newStatus,
        completionRewards
      });

      // 완료 상태인 경우 (confirmed, delayed, exempted)
      if (['confirmed', 'delayed', 'exempted'].includes(newStatus)) {
        await this.grantCompletionRewards(assignmentId, studentId, completionRewards);
      } else {
        // 미완료 상태인 경우 (pending, excluded)
        await this.revokeCompletionRewards(assignmentId, studentId, completionRewards);
      }
    } catch (error) {
      console.error('❌ 보상 처리 실패:', error);
      throw error;
    }
  }
}
